export interface IResizeResult {
    file: File | Blob | null;
    didResize: boolean;
}

module ImageTools {

    const hasBlobConstructor = typeof (Blob) !== "undefined" && ((() => {
        try {
            return Boolean(new Blob());
        } catch (e) {
            return false;
        }
    })());

    const hasArrayBufferViewSupport = hasBlobConstructor && typeof (Uint8Array) !== "undefined" && ((() => {
        try {
            return new Blob([new Uint8Array(100)]).size === 100;
        } catch (e) {
            return false;
        }
    })());

    const hasToBlobSupport = (typeof HTMLCanvasElement !== "undefined" ? HTMLCanvasElement.prototype.toBlob : false);

    const hasBlobSupport = (hasToBlobSupport || (typeof Uint8Array !== "undefined" && typeof ArrayBuffer !== "undefined" && typeof atob !== "undefined"));

    const hasReaderSupport = (typeof FileReader !== "undefined" || typeof URL !== "undefined");

    export const isSupported = (): boolean => {
        return (typeof (HTMLCanvasElement) !== "undefined") && hasBlobSupport && hasReaderSupport;
    }

    export const testToSeeIfImage = (file: File): Promise<boolean> => {
        return new Promise((resolve: (result: boolean) => void, reject: (error: any) => void) => {
            const url = window.URL || window.webkitURL;
            const image = new Image();
            image.onload = () => {
                resolve(true);
            };
            image.onerror = () => {
                resolve(false);
            };
            image.src = url.createObjectURL(file);
        });
    }

    //export const resize = (file: File, maxDimensions: { width: number; height: number }, callback: (file: File | Blob | null, didResize: boolean) => void) => {
    export const resize = (file: File, maxDimensions: { width: number; height: number }): Promise<IResizeResult> => {
        return new Promise((resolve: (result: IResizeResult) => void, reject: (error: any) => void) => {

            if (!isSupported() || !file.type.match(/image.*/)) {
                reject("not an image");
                return;
            }

            if (file.type.match(/image\/gif/)) {
                // might be animated GIF - do we reject or just not try to resize?
                //reject("Resizing GIFs not supported");
                console.log("+++ No resizing GIFs");
                resolve({ file, didResize: false });
                return;
            }

            const image: HTMLImageElement = document.createElement("img");

            loadImage(image, file).then(() => {
                //console.log("image = ", image);

                let width = image.width;
                let height = image.height;
                let isTooLarge = false;

                if (width >= height && width > maxDimensions.width) {
                    // width is the largest dimension, and it's too big.
                    height *= maxDimensions.width / width;
                    width = maxDimensions.width;
                    isTooLarge = true;
                } else if (height > maxDimensions.height) {
                    // either width wasn't over-size or height is the largest dimension
                    // and the height is over-size
                    width *= maxDimensions.height / height;
                    height = maxDimensions.height;
                    isTooLarge = true;
                }

                if (!isTooLarge) {
                    // early exit; no need to resize
                    console.log(`+++ Skip resize - small enough: ${width} x ${height} < ${maxDimensions.width} x ${maxDimensions.height}`);
                    resolve({ file, didResize: false });
                    return;
                }

                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext("2d");
                ctx?.drawImage(image, 0, 0, width, height);

                if (hasToBlobSupport) {
                    canvas.toBlob((blob) => {
                        resolve({ file: blob, didResize: true });
                    }, file.type);
                } else {
                    const blob = toBlob(canvas, file.type);
                    resolve({ file: blob, didResize: true });
                }
                // dispose of image? - browser should dispose when it goes out of scope...
            });
        });
    }

    export const toBlob = (canvas: HTMLCanvasElement, type: string) => {
        const dataUri = canvas.toDataURL(type);
        const dataUriParts = dataUri.split(",");
        let byteString;
        if (dataUriParts[0].indexOf("base64") >= 0) {
            // Convert base64 to raw binary data held in a string:
            byteString = atob(dataUriParts[1]);
        } else {
            // Convert base64/URLEncoded data component to raw binary data:
            byteString = decodeURIComponent(dataUriParts[1]);
        }
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const intArray = new Uint8Array(arrayBuffer);

        for (let i = 0; i < byteString.length; i += 1) {
            intArray[i] = byteString.charCodeAt(i);
        }

        const mimeString = dataUriParts[0].split(":")[1].split(";")[0];
        const blob = new Blob(
            [hasArrayBufferViewSupport ? intArray : arrayBuffer],
            { type: mimeString }
        );

        return blob;
    }

    export const loadImage = (image: HTMLImageElement, file: File): Promise<void> => {
        return new Promise((resolve: () => void) => {
            const reader = new FileReader();
            reader.onload = evt => {
                const src = (evt.target)?.result;
                if (typeof src === "string") {
                    image.src = src;
                } else {
                    console.warn("+++ src not string: ", src);
                }
                resolve();
            }
            reader.readAsDataURL(file);
        });
    }
}

export default ImageTools;